import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ActionButton from '../../../components/shared/ActionButton';
import Button from '../../../components/shared/Button';
import Table from '../../../components/shared/table/Table';
import ExportIcon from '../../../assets/icons/DownloadIcon';
import KitIcon from '../../../assets/icons/KitIcon';

import {
  exportKitAvailabilityReport,
  getKitAvailabilityReport,
} from '../../../actions/setsActions';
import { setAllocationStatuses } from '../../../constants/enums';
import useAlert from '../../../hooks/useAlert';
import {
  blueBorderColor,
  greenBorderColor,
  lightBlueColor,
  lightGreenColor,
  lightOrangeColor,
  lightRedColor,
  orangeBorderColor,
  redBorderColor,
} from '../../../utils/colors';

import './kit-availability-report.scss';

const availabilityStyles = {
  GOOD: {
    background: lightGreenColor,
    border: `1px solid ${greenBorderColor}`,
    color: greenBorderColor,
  },
  MODERATE: {
    background: lightBlueColor,
    border: `1px solid ${blueBorderColor}`,
    color: blueBorderColor,
  },
  LOW: {
    background: lightOrangeColor,
    border: `1px solid ${orangeBorderColor}`,
    color: orangeBorderColor,
  },
  'NOT AVAILABLE': {
    background: lightRedColor,
    border: `1px solid ${redBorderColor}`,
    color: redBorderColor,
  },
};

const KitAvailabilityReportingPage = () => {
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const tenantColor = useSelector((state) => state.tenant.currentTenant.colorPrimary);

  const [report, setReport] = useState({
    rows: [],
    thresholds: { low: 0.2, good: 0.5 },
  });
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const result = await dispatch(getKitAvailabilityReport());
      setReport({
        rows: result?.rows || [],
        thresholds: result?.thresholds || { low: 0.2, good: 0.5 },
      });
    } catch (err) {
      console.error(err);
      showAlert('error', err.message || 'Unable to generate the kit availability report');
    } finally {
      setLoading(false);
    }
  }, [dispatch, showAlert]);

  useEffect(() => {
    loadReport().catch((err) => console.error(err));
  }, [loadReport]);

  const onExport = async () => {
    setExportLoading(true);
    try {
      await dispatch(exportKitAvailabilityReport());
    } catch (err) {
      console.error(err);
      showAlert('error', err.message || 'Unable to export the kit availability report');
    } finally {
      setExportLoading(false);
    }
  };

  const columns = useMemo(() => [
    { title: 'kit name', field: 'kitName', type: 'default', sort: true },
    { title: 'kit id', field: 'kitId', type: 'default', sort: true },
    { title: 'allocated', field: 'allocated', type: 'default', sort: true },
    { title: 'available', field: 'available', type: 'default', sort: true },
    {
      title: 'available but incomplete',
      field: 'availableButIncomplete',
      type: 'default',
      sort: true,
    },
    { title: 'quarantined', field: 'quarantined', type: 'default', sort: true },
    { title: 'total sets', field: 'totalSets', type: 'default', sort: true },
    {
      title: 'available %',
      field: 'availabilityPercent',
      type: 'custom',
      sort: true,
      formatter: (value) => `${Math.round((value || 0) * 100)}%`,
    },
    {
      title: 'availability',
      field: 'availability',
      type: 'custom',
      sort: true,
      formatter: (value) => (
        <span
          className='kit-availability-report__status'
          style={availabilityStyles[value] || {}}
        >
          {value}
        </span>
      ),
    },
  ], []);

  const lowThreshold = Math.round((report.thresholds?.low || 0.2) * 100);
  const goodThreshold = Math.round((report.thresholds?.good || 0.5) * 100);

  return (
    <div className='page-container reporting-page__container kit-availability-report'>
      <div className='page-title'>Kit Availability</div>

      <div className='reporting-page__body'>
        <div className='kit-availability-report__controls'>
          <div className='kit-availability-report__description'>
            <div className='font-bold font-size-bg'>Fleet availability</div>
            <div className='secondary m-t-sm'>
              Active loan-fleet sets grouped by kit. Available-but-incomplete sets are shown separately and are not included in the available percentage.
            </div>
            <div className='kit-availability-report__thresholds'>
              <span style={availabilityStyles['NOT AVAILABLE']}>0% Not Available</span>
              <span style={availabilityStyles.LOW}>Under {lowThreshold}% Low</span>
              <span style={availabilityStyles.MODERATE}>{lowThreshold}–{goodThreshold - 1}% Moderate</span>
              <span style={availabilityStyles.GOOD}>{goodThreshold}%+ Good</span>
            </div>
          </div>

          <div className='kit-availability-report__actions'>
            <Button
              onClick={loadReport}
              disabled={loading}
              loading={loading}
              text='Run Report'
              width='160px'
            />
            <ActionButton
              onClick={onExport}
              bgColor={tenantColor || setAllocationStatuses.AVAILABLE.color}
              disabled={exportLoading || loading}
              loading={exportLoading}
            >
              <ExportIcon color='#ffffff' />
              <span>Export</span>
            </ActionButton>
          </div>
        </div>

        <div className='cases-table__container kit-availability-report__table'>
          <div className='d-flex m-b-lg'>
            <KitIcon color='var(--app-text)' />
            <div className='font-size-bg font-bold m-l-md'>Kit Fleet</div>
          </div>

          <Table
            columns={columns}
            rows={report.rows}
            checkboxes={false}
            pagination
            loading={loading}
          />

          {!loading && !report.rows.length && (
            <div className='secondary text-center p-lg'>No active loan-fleet sets found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KitAvailabilityReportingPage;
